/**
 * Quotas tab — read-only daily ETA and tile call counters for the selected
 * tenant, plus a destructive "purge tile cache" button.
 *
 * @module panels/shuttle/quotas
 */
import * as api from "../../api/shuttle.ts";
import type { QuotaSnapshot } from "../../types/shuttle.ts";
import type { TabContext } from "./index.ts";

export async function renderQuotasTab(
	root: HTMLElement,
	ctx: TabContext,
): Promise<void> {
	let q: QuotaSnapshot;
	try {
		q = await api.getQuotas(ctx.tenantSlug);
	} catch (e) {
		root.innerHTML = `<div class="shuttle-banner error">Failed to load quotas: ${escapeHtml((e as Error).message)}</div>`;
		return;
	}

	const etaUsed = q.eta_calls_today ?? 0;
	const tileUsed = q.tile_calls_today ?? 0;
	const etaPct =
		q.daily_eta_budget > 0
			? Math.min(100, (etaUsed / q.daily_eta_budget) * 100)
			: 0;
	const tilePct =
		q.daily_tile_budget > 0
			? Math.min(100, (tileUsed / q.daily_tile_budget) * 100)
			: 0;

	root.innerHTML = `
    <div class="panel-header"><h3>Quotas — ${escapeHtml(q.date)}</h3></div>
    <div class="shuttle-grid-2">
      <div class="shuttle-quota-card">
        <div class="shuttle-quota-label">Routes API (ETA) calls</div>
        <div class="shuttle-quota-value">${etaUsed.toLocaleString()} / ${q.daily_eta_budget.toLocaleString()}</div>
        <div class="shuttle-quota-bar">
          <div class="shuttle-quota-fill" style="width:${etaPct.toFixed(1)}%"></div>
        </div>
      </div>
      <div class="shuttle-quota-card">
        <div class="shuttle-quota-label">Map Tile fetches (cache misses)</div>
        <div class="shuttle-quota-value">${tileUsed.toLocaleString()} / ${q.daily_tile_budget.toLocaleString()}</div>
        <div class="shuttle-quota-bar">
          <div class="shuttle-quota-fill" style="width:${tilePct.toFixed(1)}%"></div>
        </div>
      </div>
    </div>

    <div class="panel-header" style="margin-top:1.5rem"><h3>Cache</h3></div>
    <div class="shuttle-form-actions">
      <button id="purge-cache" class="shuttle-btn danger">Purge tile cache</button>
      <span class="panel-hint">Deletes all on-disk tiles + the Google session token for this tenant. Next requests re-create the session.</span>
    </div>
  `;

	root.querySelector("#purge-cache")?.addEventListener("click", async () => {
		if (!confirm(`Purge the tile cache for ${ctx.tenantSlug}?`)) return;
		try {
			await api.purgeCache(ctx.tenantSlug);
			alert("Cache purged");
			await ctx.rerender();
		} catch (e) {
			alert((e as Error).message);
		}
	});
}

function escapeHtml(s: string): string {
	return s.replace(
		/[&<>"']/g,
		(c) =>
			({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
				c
			]!,
	);
}
